import React, { Fragment, useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { ChartCatalog } from "../ChartCatalog";
import { getAvailableChartDefinitions, ChartCategory, SizeTier } from "../charts";

interface BoardChartsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onChartDragStart: (type: string, sizeTier: SizeTier, aspectRatio: string) => void;
    onChartDragEnd?: () => void;
    onChartClick: (type: string) => void;
    onSaveTemplate?: () => void;
    isHidden?: boolean;
}

const CATEGORIES: (ChartCategory | 'All')[] = [
    'All',
    'Financial',
    'Operations',
    'Marketing',
    'Growth',
    'Decision',
    'Customer Behavior',
    'Risk',
    'Prediction'
];

export const BoardChartsModal: React.FC<BoardChartsModalProps> = ({
    isOpen,
    onClose,
    onChartDragStart,
    onChartDragEnd,
    onChartClick,
    onSaveTemplate,
    isHidden = false,
}) => {
    const { t } = useTranslation("common");
    const [selectedCategory, setSelectedCategory] = useState<ChartCategory | 'All'>('All');
    const [searchTerm, setSearchTerm] = useState("");
    const scrollRef = useRef<HTMLDivElement>(null);
    const [lastScrollPos, setLastScrollPos] = useState(0);

    // Filter charts based on category and search term
    const filteredCharts = useMemo(() => {
        let charts = getAvailableChartDefinitions();
        
        // Category filter
        if (selectedCategory !== 'All') {
            charts = charts.filter(chart => chart.categories.includes(selectedCategory as ChartCategory));
        }

        // Search filter
        if (searchTerm.trim()) {
            const query = searchTerm.toLowerCase();
            charts = charts.filter(chart => 
                chart.title.toLowerCase().includes(query) || 
                chart.description?.toLowerCase().includes(query) ||
                t(`charts.titles.${chart.type}`).toLowerCase().includes(query) ||
                t(`charts.descriptions.${chart.type}`).toLowerCase().includes(query)
            );
        }

        return charts;
    }, [selectedCategory, searchTerm, t]);

    // Restore scroll position when reopening
    useEffect(() => {
        if (isOpen && !isHidden && scrollRef.current) {
            // Use a small timeout to ensure content is rendered
            const timer = setTimeout(() => {
                if (scrollRef.current) {
                    scrollRef.current.scrollTop = lastScrollPos;
                }
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [isOpen, isHidden, lastScrollPos]);

    // Save scroll position when closing
    useEffect(() => {
        if (!isOpen && scrollRef.current) {
            setLastScrollPos(scrollRef.current.scrollTop);
        }
    }, [isOpen]);

    // Handle Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isHidden) {
                onClose();
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleEscape);
        }
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isOpen, isHidden, onClose]);

    // Map internal category name to translation key
    const getCategoryKey = (cat: string) => {
        const mapping: Record<string, string> = {
            'All': 'all',
            'Financial': 'financial',
            'Operations': 'operations',
            'Marketing': 'marketing',
            'Growth': 'growth',
            'Decision': 'decision',
            'Customer Behavior': 'customerBehavior',
            'Risk': 'risk',
            'Prediction': 'prediction'
        };
        return mapping[cat] || cat.toLowerCase();
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className={`relative z-50 ${isHidden ? 'pointer-events-none' : ''}`} onClose={!isHidden ? onClose : () => { }}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 modal-backdrop" />
                </Transition.Child>

                <div className={`fixed inset-0 overflow-y-auto ${isHidden ? 'pointer-events-none' : ''}`}>
                    {isHidden && (
                        <button
                            type="button"
                            className="sr-only"
                            aria-hidden="true"
                            tabIndex={0}
                            style={{ position: 'fixed', top: 0, left: 0 }}
                        >
                            Focus Trap Target
                        </button>
                    )}

                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel
                                className="w-full max-w-6xl transform"
                            >
                                <div className={`bg-neutral-50 dark:bg-neutral-900 rounded-2xl shadow-2xl h-[90vh] sm:h-[85vh] flex flex-col overflow-hidden border border-white/20 dark:border-neutral-800 ${isHidden ? 'pointer-events-none' : 'pointer-events-auto'}`}>
                                    {/* Header */}
                                    <div className="px-5 sm:px-8 pt-6 pb-4 sm:pb-6 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 flex flex-col gap-5 sm:gap-6 shrink-0">
                                        <div className="flex justify-between items-start">
                                            <div className="pr-4 flex-1">
                                                <Dialog.Title as="h2" className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
                                                    {t("charts.addCharts")}
                                                </Dialog.Title>
                                                <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-1 line-clamp-1 sm:line-clamp-none">
                                                    {t("charts.dragInstructions")}
                                                </p>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                {/* Search Bar */}
                                                <div className="relative group min-w-[200px] sm:min-w-[300px]">
                                                    <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none">
                                                        <span className="w-4 h-4 text-neutral-400 group-focus-within:text-accent-primary transition-colors duration-200" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_d872171d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_d872171d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        placeholder={t("charts.searchPlaceholder") || "Search charts..."}
                                                        className="w-full bg-neutral-100/50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-xl py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary transition-all placeholder:text-neutral-400"
                                                    />
                                                    {searchTerm && (
                                                        <button 
                                                            onClick={() => setSearchTerm("")}
                                                            className="absolute inset-y-0 right-3 flex items-center text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
                                                        >
                                                            <span className="w-3.5 h-3.5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_37002b9f.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_37002b9f.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                                        </button>
                                                    )}
                                                </div>

                                                <button
                                                    onClick={onClose}
                                                    className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 p-2 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all duration-200 shrink-0"
                                                >
                                                    <span className="w-5 h-5 sm:w-6 h-6" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_37002b9f.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_37002b9f.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Tabs Container - Modern Segmented Control */}
                                        <div className="relative">
                                            <div className="flex items-center overflow-x-auto scrollbar-hide scroll-smooth -mx-5 px-5 sm:mx-0 sm:px-0">
                                                <div className="flex p-1 bg-neutral-100/80 dark:bg-neutral-800/80 rounded-2xl border border-neutral-200/50 dark:border-neutral-700/50 backdrop-blur-sm min-w-max">
                                                    {CATEGORIES.map((cat) => (
                                                        <button
                                                            key={cat}
                                                            onClick={() => setSelectedCategory(cat)}
                                                            className={`px-4 sm:px-5 py-2 rounded-xl text-xs sm:text-sm font-bold whitespace-nowrap transition-all duration-300 relative ${selectedCategory === cat
                                                                ? 'bg-white dark:bg-neutral-700 text-accent-primary shadow-sm shadow-accent-primary/10 ring-1 ring-black/[0.02]'
                                                                : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-white/40 dark:hover:bg-neutral-600/40'
                                                                }`}
                                                        >
                                                            {t(`charts.categories.${getCategoryKey(cat)}`)}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div 
                                        ref={scrollRef}
                                        className="flex-1 overflow-y-auto custom-scrollbar bg-neutral-50/30 dark:bg-neutral-900/30"
                                    >
                                        <ChartCatalog
                                            onDragStart={onChartDragStart}
                                            onDragEnd={onChartDragEnd}
                                            onClick={onChartClick}
                                            selectedCategory={selectedCategory}
                                            filteredCharts={filteredCharts}
                                        />
                                    </div>

                                    {/* Footer */}
                                    <div className="px-5 sm:px-8 py-4 sm:py-5 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center shrink-0">
                                        <div className="flex items-center gap-2.5 text-neutral-400 dark:text-neutral-500 text-[10px] sm:text-xs">
                                            <div className="w-5 h-5 flex items-center justify-center bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-100 dark:border-neutral-800 shrink-0">
                                                <span className="w-3 h-3" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_80999440.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_80999440.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                            </div>
                                            <span className="font-medium text-neutral-500 dark:text-neutral-400">{t("charts.syncInfo") || "Charts sync automatically with your board data."}</span>
                                        </div>
                                        <button
                                            onClick={onSaveTemplate}
                                            className="flex items-center justify-center gap-2 px-6 py-2.5 text-xs sm:text-sm font-bold text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:border-accent-primary/50 dark:hover:border-accent-primary/50 hover:bg-accent-primary/5 dark:hover:bg-accent-primary/10 hover:text-accent-primary rounded-xl transition-all duration-300 shadow-sm active:scale-[0.98] group w-full sm:w-auto"
                                        >
                                            <span className="w-4 h-4 text-neutral-400 dark:text-neutral-600 group-hover:text-accent-primary transition-colors shrink-0" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_1325d40b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_1325d40b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                            <span>{t("boards.savePageAsTemplate") || "Save as Template"}</span>
                                        </button>
                                    </div>

                                </div>
                            </Dialog.Panel>

                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
